import React, { useState, useEffect, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";

import "../assets/fonts/fonts.css";
import img from "../assets/images/projects.png";
import fps from "../assets/images/fps.png";
import acec from "../assets/images/acec.gif";
import diagnos from "../assets/images/diagnos.gif";
import yuchen from "../assets/images/yuchen.png";
import stats from "../assets/images/stats.png";
import exergame from "../assets/images/exergame.png";
import calc from "../assets/images/calc.png";
import yashy from "../assets/images/yashy.png";
import surge from "../assets/images/surge.gif";
import { tempFixedScroll } from "../functions/scrollUtils";

import NavbarFixed from "../components/NavbarFixed";
import Footer from "../components/Footer";
import ScrollDown from "../components/ScrollDown";
import Project from "../components/projects/Project";
import YearLabel from "../components/projects/YearLabel";

// 🔥 Firestore imports
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";

export default function Projects() {
  const [, setScrollY] = useState(0);
  const [projectsTextOpacity, setProjectsTextOpacity] = useState(1);
  const [visibleSection, setVisibleSection] = useState(0);

  // 🔥 projects from Firestore
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // For IntersectionObserver
  const projectRefs = useRef([]);

  // Timeline ref for fixed positioning
  const timelineRef = useRef(null);

  // Dynamic year refs for scrolling
  const yearRefs = useRef({});

  const [isOpen, setIsOpen] = useState(false); // Dropdown state

  // Map Firestore imageKey -> imported image
  const imageMap = {
    fps,
    acec,
    diagnos,
    yuchen,
    stats,
    exergame,
    calc,
    yashy,
    surge,
  };

  // 🔥 1) Load projects from Firestore
  useEffect(() => {
    const q = query(
      collection(db, "projects"),
      orderBy("yearSort", "desc"), // newest years first
      orderBy("projectSort", "desc") // order inside each year
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc, index) => {
        const d = doc.data();
        return {
          id: doc.id,
          index, // global index in display order
          ...d,
        };
      });

      setProjects(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // 🔢 List of years (in the order they appear in the sorted projects)
  const years = useMemo(
    () => Array.from(new Set(projects.map((p) => p.yearLabel))),
    [projects]
  );

  // Scroll to a given year
  const executeScroll = (year) => {
    const ref = yearRefs.current[year];
    if (ref) {
      ref.scrollIntoView({
        block: "start",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  };

  // Existing scroll handler for fixed timeline scroll behavior
  useEffect(() => {
    const timelineStart = 101;
    const timelineEnd = 1000000;

    const handleScroll = () => {
      const timelineFixed =
        7 + (7 * (876 - window.innerHeight)) / window.innerHeight;
      tempFixedScroll(timelineRef, timelineStart, timelineFixed, timelineEnd);
      setScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  // Fade out the "Projects" text on scroll (unchanged)
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const fadePoint = window.innerHeight / 2;

      if (scrollPosition >= fadePoint) {
        setProjectsTextOpacity(0); // Fade out
      } else {
        setProjectsTextOpacity(1); // Fade in
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // 🔭 IntersectionObserver to track which project is visible
  // NOTE: Now depends on `projects` so it runs after data is loaded
  useEffect(() => {
    if (!projects.length) return;

    const projRefCurr = projectRefs.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = projRefCurr.indexOf(entry.target);
            if (index !== -1) {
              setVisibleSection(index);
            }
          }
        });
      },
      {
        threshold: 0.6, // Trigger when 60% of the element is visible
      }
    );

    const refs = projRefCurr.filter(Boolean);
    refs.forEach((ref) => observer.observe(ref));

    return () => {
      refs.forEach((ref) => observer.unobserve(ref));
    };
  }, [projects]);

  const getBackgroundColor = () => {
    const colors = [
      "rgba(16, 26, 43, 0.9)",
      "rgba(105, 54, 104, 0.9)",
      "rgba(0, 119, 182, 0.9)",
      "rgba(51, 30, 56, 0.9)",
      "rgba(20, 82, 102, 0.9)",
      "rgba(16, 26, 43, 0.9)",
      "rgba(105, 54, 104, 0.9)",
      "rgba(0, 119, 182, 0.9)",
      "rgba(51, 30, 56, 0.9)",
      "rgba(20, 82, 102, 0.9)",
      "rgba(16, 26, 43, 0.9)",
      "rgba(105, 54, 104, 0.9)",
      "rgba(0, 119, 182, 0.9)",
      "rgba(51, 30, 56, 0.9)",
      "rgba(20, 82, 102, 0.9)",
      "rgba(16, 26, 43, 0.9)",
      "rgba(105, 54, 104, 0.9)",
      "rgba(0, 119, 182, 0.9)",
      "rgba(51, 30, 56, 0.9)",
      "rgba(20, 82, 102, 0.9)",
    ];

    if (!projects.length) return colors[0];
    return colors[visibleSection % colors.length];
  };

  return (
    <div>
      <Helmet>
        <title>moses.zhang.projects</title>
      </Helmet>
      <NavbarFixed visible={true} />
      {/* Section 1: Static Background */}
      <section className="relative h-screen bg-fixed bg-black bg-opacity-100 z-[-1] bg-center bg-cover">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 2 }}
          className="fixed inset-0 flex justify-center items-center z-[-2]"
          style={{
            backgroundImage: `url(${img})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundColor: "rgba(0,0,0,0.4)",
            backgroundBlendMode: "darken",
          }}
        >
          <motion.h1
            initial={{ opacity: 1 }}
            animate={{ opacity: projectsTextOpacity }} // Bind opacity to the scroll
            className="text-white text-6xl font-bold"
          >
            Projects
          </motion.h1>
        </motion.div>
      </section>
      <ScrollDown delay={0.6} />

      <motion.div
        id="proj-list"
        className="min-h-screen"
        animate={{ backgroundColor: getBackgroundColor() }}
        transition={{ duration: 0.5 }}
      >
        {/* Content */}
        <div className="p-8 md:px-24 md:py-16 border-y-2 border-slate-600">
          {loading ? (
            <div className="text-white">Loading projects...</div>
          ) : (
            <div className="space-y-12 md:space-y-20">
              {/* 🔁 Render each year + its projects */}
              {years.map((year) => (
                <React.Fragment key={year}>
                  <div
                    className="scroll-m-32"
                    ref={(el) => {
                      if (el) yearRefs.current[year] = el;
                    }}
                  >
                    <YearLabel year={year} />
                  </div>

                  {projects
                    .filter((p) => p.yearLabel === year)
                    .map((project) => (
                      <div
                        key={project.id}
                        className="scroll-m-32"
                        ref={(el) => {
                          if (el) {
                            projectRefs.current[project.index] = el;
                          }
                        }}
                      >
                        <Project
                          title={project.title}
                          date={project.date}
                          body={
                            <div
                              dangerouslySetInnerHTML={{
                                __html: project.body,
                              }}
                            />
                          }
                          image={imageMap[project.imageKey] || img}
                          isLeft={project.index % 2 === 0}
                        />
                      </div>
                    ))}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* Timeline fixed on the left side */}
        <div
          className="absolute top-[101h] left-0 ml-2 h-full w-16 bg-transparent"
          ref={timelineRef}
        >
          <>
            {/* For medium and larger screens, show the list */}
            <ul className="hidden md:block text-left text-white pl-4 pt-2">
              {years.map((year) => (
                <motion.li
                  key={year}
                  onClick={() => executeScroll(year)}
                  className="my-2.5 md:my-3 bg-white bg-opacity-10 inline-block px-2 py-1 md:px-3 md:py-1.5 rounded-lg cursor-pointer text-sm md:text-xl nsans-7-bo"
                  whileHover={{
                    scale: 1.1,
                    backgroundColor: "rgba(255,255,255,0.2)",
                  }}
                  transition={{ type: "spring", stiffness: 100, mass: 0.5 }}
                >
                  {year}
                </motion.li>
              ))}
            </ul>

            {/* For small screens, show a dropdown */}
            <div className="block md:hidden text-left text-white pl-0.5 pt-2">
              <div className="relative inline-block w-full">
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="bg-white bg-opacity-15 whitespace-nowrap px-2.5 py-1 rounded-lg text-left text-sm nsans-7-sc-li shadow-lg"
                >
                  {isOpen ? "Years ▲" : "Years ▼"}
                </button>

                {isOpen && (
                  <ul className="absolute mt-2 rounded-lg inline-block z-10">
                    {years.map((year) => (
                      <motion.li
                        key={year}
                        onClick={() => executeScroll(year)}
                        className="my-2 px-2 py-1 bg-white bg-opacity-5 inline-block rounded-lg cursor-pointer text-sm nsans-7-eli text-white hover:bg-opacity-20 shadow-lg"
                        whileHover={{ scale: 1.1 }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{
                          type: "spring",
                          stiffness: 100,
                          mass: 0.5,
                        }}
                      >
                        {year}
                      </motion.li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </>
        </div>
      </motion.div>
      <Footer />
    </div>
  );
}
